import { ReactNode } from "react";
 import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "./Header";
import Footer from "./Footer";
import StickyMobileCTA from "./StickyMobileCTA";
import StickyQuoteBar from "@/components/shared/StickyQuoteBar";
import FloatingChatButton from "@/components/shared/FloatingChatButton";
import ExitIntentPopup from "@/components/shared/ExitIntentPopup";
import RecentActivityToast from "@/components/shared/RecentActivityToast";
import SocialProofNotification from "@/components/shared/SocialProofNotification";
import JsonLdSchema from "@/components/seo/JsonLdSchema";
import { useEngagementBeacon } from "@/hooks/useEngagementBeacon";
import { useDomainBranding } from "@/hooks/useDomainBranding";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface PublicLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  breadcrumbs?: BreadcrumbItem[];
  includeBusinessSchemas?: boolean;
}

const PublicLayout = ({ 
  children, 
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  breadcrumbs,
  includeBusinessSchemas = false
}: PublicLayoutProps) => {
   const location = useLocation();
   const branding = useDomainBranding();

   // Normalize path for canonical URL (same rules as UrlNormalizer)
   const rawPath = location.pathname;
   let normalizedPath = rawPath;
   
   // Strip trailing slash
   if (normalizedPath.length > 1 && normalizedPath.endsWith("/")) {
     normalizedPath = normalizedPath.slice(0, -1);
   }
   // Strip roofing- prefix from /roofing/roofing-* slugs
   const prefixMatch = normalizedPath.match(/^\/roofing\/roofing-(.+)$/);
   if (prefixMatch) normalizedPath = `/roofing/${prefixMatch[1]}`;
   // Fire engagement beacon (scroll + time) on every page
   useEngagementBeacon();

   const autoCanonical = `${branding.canonicalOrigin}${normalizedPath === "/" ? "" : normalizedPath}`;
   // If explicit canonical is relative, make it absolute against the active brand origin
   const canonicalUrl = canonical
     ? canonical.startsWith("http") ? canonical : `${branding.canonicalOrigin}${canonical}`
     : autoCanonical;
   
  // Resolve title — prefer explicit, else brand default
  const resolvedTitle = title || branding.defaultTitle;
  const resolvedDescription = description || branding.defaultDescription;
  const fullTitle = resolvedTitle.includes(branding.siteName)
    ? resolvedTitle
    : `${branding.siteName} | ${resolvedTitle}`;
  
  // Breadcrumb schema for better search result display
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: {
        "@type": "WebPage",
        "@id": item.url
      }
    }))
  } : null;

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={resolvedDescription} />
         <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={resolvedDescription} />
        <meta property="og:type" content={ogType} />
        <meta property="og:site_name" content={branding.siteName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content={ogImage || branding.ogImage} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={resolvedDescription} />
        <meta name="twitter:image" content={ogImage || branding.ogImage} />
        
        {/* Breadcrumb Schema */}
        {breadcrumbSchema && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        )}
      </Helmet>

      {/* Global RoofingContractor + WebSite + Organization entity graph (OBC only) */}
      {branding.brand === "obc" && <JsonLdSchema />}

      
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 pt-[104px] md:pt-[120px] pb-16 md:pb-0">
          {children}
        </main>
        <Footer />
        <StickyMobileCTA />
        <StickyQuoteBar />
        
        <FloatingChatButton />
        <ExitIntentPopup />
        <RecentActivityToast />
        <SocialProofNotification />
      </div>
    </>
  );
};

export default PublicLayout;
